import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import {
  AppBar,
  Toolbar,
  Drawer,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { animated } from "react-spring";

import Menu from "../icons/Menu";

const StyledAppBar = styled(AppBar)({
  background: "linear-gradient(135deg, rgba(12, 84, 112, 0.95) 0%, rgba(66, 139, 202, 0.95) 100%)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
});

const StyledToolbar = styled(Toolbar)({
  display: "flex",
  justifyContent: "space-between",
  overflow: "hidden",
  minHeight: "70px",
});

const MenuRight = styled('div')({
  display: "flex",
  alignItems: "center",
  marginLeft: "auto",
});

const DrawerContent = styled('div')({
  width: 250,
  padding: "20px",
  "& a, p, div": {
    color: "#0c5470",
    fontFamily: "'Inter', sans-serif",
    fontSize: "16px",
    textTransform: "uppercase",
    textDecoration: "none",
    margin: "10px 0",
    display: "block",
    "&:hover": {
      color: "#20c997",
    },
  },
});

const Header = ({
  children,
  appbarProps,
  toolbarProps,
  drawerProps,
  menuWidth = 1100,
}) => {
  const [isOpen, setOpen] = useState(false);
  const matchesLargeScreen = useMediaQuery(`(min-width: ${menuWidth}px)`);

  useEffect(() => {
    if (matchesLargeScreen) {
      setOpen(false);
    }
  }, [matchesLargeScreen]);

  const arrayChildren = React.Children.toArray(children);

  // menu links
  const menuLinks = arrayChildren.filter(
    (child) => !child.props.showOnlyInDrawer && !child.props.showWhenCollapsed
  );
  const linksRight = menuLinks.filter((child) => child.props.right);

  // logo without menu links
  const noLinks = arrayChildren.filter(
    (child) => child.props.showWhenCollapsed
  );

  // drawer
  const drawerLinks = arrayChildren.filter(
    (child) => !child.props.showWhenCollapsed && !child.props.showOnlyInHeader
  );

  return (
    <StyledAppBar position="static" {...appbarProps}>
      <StyledToolbar {...toolbarProps}>
        {noLinks}

        {!matchesLargeScreen && (
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={() => setOpen(true)}
            sx={{ marginLeft: 'auto' }}
          >
            <Menu />
          </IconButton>
        )}

        {matchesLargeScreen && (
          <animated.div>
            <MenuRight>{linksRight}</MenuRight>
          </animated.div>
        )}

        <Drawer
          anchor="right"
          open={isOpen}
          onClose={() => setOpen(false)}
          {...drawerProps}
        >
          <DrawerContent
            tabIndex={0}
            role="button"
            onClick={() => setOpen(false)}
            onKeyDown={() => setOpen(false)}
          >
            {drawerLinks}
          </DrawerContent>
        </Drawer>
      </StyledToolbar>
    </StyledAppBar>
  );
};

Header.displayName = "Header";

Header.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  appbarProps: PropTypes.object,
  toolbarProps: PropTypes.object,
  drawerProps: PropTypes.object,
  menuWidth: PropTypes.number,
};

export default Header;
